/**
 * Cross-service RBAC core (PB-9 / IA-06): the privilege-decision primitives
 * shared by every backend service. Pure — no NestJS, no Prisma, no I/O — so it
 * is safe to import from the `./rbac` subpath without pulling `@nestjs/common`
 * (unlike the package root `.`, which re-exports the NestJS adapter).
 *
 * Endpoints declare WHICH privilege an action needs (not which roles are
 * allowed); each module maps its own role names to privilege lists. Adding a
 * role is a one-line change to that map — every action stays the same.
 *
 * The v2 authorization model (Authorization & Collaboration Model v2) adds two
 * computed layers on top of the base role→privilege map, both implemented
 * here so every enforcement point shares ONE decision:
 *
 *  - the Facilitator OVERLAY — a persistent, owner-granted privilege boost
 *    (never a role/enum/wire value) computed as the owner's privileges minus
 *    a fixed exclusion set; and
 *  - the LOCK edit-gate — canvas/phase locks restrict lock-bound actors only
 *    (in practice Collaborators); Owners and Facilitator-holders are never
 *    lock-gated, Viewers never hold `edit` in the first place.
 */
/**
 * The privileges a role grants, or `undefined` when the role is not defined in
 * this module's map. Pure lookup — callers decide how to treat `undefined`
 * (e.g. throw an "unknown role" error).
 *
 * Uses an own-property check so prototype keys (`"__proto__"`, `"constructor"`,
 * `"hasOwnProperty"`, …) resolve to `undefined` (a clean deny) rather than
 * returning inherited prototype values that would crash `roleGrants`.
 */
export function resolvePrivileges(privileges, role) {
    return Object.prototype.hasOwnProperty.call(privileges, role)
        ? privileges[role]
        : undefined;
}
/**
 * `true` iff `role` is defined in `privileges` AND its privilege list includes
 * `requires`. An unknown role or a role granting no privileges both yield
 * `false`. This is the single decision both the HTTP guard and the AI-tools
 * path consume.
 */
export function roleGrants(privileges, role, requires) {
    const granted = resolvePrivileges(privileges, role);
    return granted !== undefined && granted.includes(requires);
}
// ---------- Facilitator overlay (v2) ----------
/**
 * The privileges a Facilitator can NEVER hold, per the v2 model: the boost is
 * "everything an Owner has except destroy/transfer the instance and
 * appoint/remove Facilitators".
 */
export const FACILITATOR_EXCLUDED = [
    "delete",
    "transfer",
    "grant_facilitator",
    "revoke_facilitator",
];
/**
 * The Facilitator overlay privilege set for a module — COMPUTED as the
 * module's `owner` privileges minus `FACILITATOR_EXCLUDED`, never hand-listed.
 * A privilege later granted to owners therefore reaches Facilitators
 * automatically; to withhold one, add it to the exclusion set.
 *
 * The `owner` role name is fixed by the v2 model (module roles are
 * owner | collaborator | viewer everywhere); a map without an `owner` row
 * yields an empty overlay (fail-closed).
 */
export function facilitatorPrivileges(privileges) {
    const owner = resolvePrivileges(privileges, "owner") ?? [];
    return owner.filter((p) => !FACILITATOR_EXCLUDED.includes(p));
}
/**
 * Roles that are exempt from the lock gate. Deliberately an allowlist (plus
 * the Facilitator overlay below): a future editing role added to a module map
 * is lock-gated BY DEFAULT unless explicitly exempted here — the safe default.
 */
const LOCK_EXEMPT_ROLES = new Set(["owner"]);
/**
 * The single v2 authorization rule — a direct transcription of the model's
 * formal `authorize()` (Authorization & Collaboration Model v2 §10):
 *
 *   1. no membership → deny (`no_role`) — no implicit access, incl. Tenant Admin;
 *   2. role absent from the map → deny (`unknown_role`, fail-closed; the
 *      overlay never rescues an unknown role);
 *   3. effective privileges = base ∪ (Facilitator overlay when held);
 *   4. action not granted → deny (`missing_privilege`);
 *   5. the lock gate applies only to an `edit` by a lock-bound actor:
 *      Owners and Facilitator-holders are never lock-gated; everyone else is
 *      denied (`locked`) while the canvas or the target's phase is locked.
 *
 * Every enforcement point (HTTP guard, canvas gates, collab authorize, later
 * the FE) calls this one function, so policy cannot drift between them.
 */
export function authorize(input) {
    const { privileges, role, isFacilitator, action, lock } = input;
    if (role === null || role === undefined || role === "") {
        return { allowed: false, reason: "no_role" };
    }
    const base = resolvePrivileges(privileges, role);
    if (base === undefined) {
        return { allowed: false, reason: "unknown_role" };
    }
    const effective = isFacilitator
        ? new Set([...base, ...facilitatorPrivileges(privileges)])
        : new Set(base);
    if (!effective.has(action)) {
        return { allowed: false, reason: "missing_privilege" };
    }
    if (action === "edit" && lock !== undefined) {
        const lockExempt = isFacilitator || LOCK_EXEMPT_ROLES.has(role);
        if (!lockExempt && (lock.canvasLocked || lock.phaseLocked)) {
            return { allowed: false, reason: "locked" };
        }
    }
    return { allowed: true };
}
//# sourceMappingURL=rbac.js.map